import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import assetTorus from '@/assets/asset-chrome-torus.webp';
import assetSpiral from '@/assets/asset-chrome-spiral.webp';
import { SpotlightCard } from '@/components/ui/spotlight-card';

const steps = ['step1', 'step2', 'step3', 'step4'] as const;

type ProcessStep = {
  title: string;
  description: string;
};

type ProcessSectionProps = {
  tag?: string;
  title?: string;
  docs?: string;
  steps?: ProcessStep[];
};

const stepIcons = [
  // Magnifying glass / Discovery
  <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7"><circle cx="14" cy="14" r="9" stroke="currentColor" strokeWidth="2.2"/><path d="M21 21l7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>,
  // Pencil ruler / Strategy
  <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7"><path d="M4 28L22 10l6 6L10 34" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18 14l6-6 4 4-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/><path d="M8 24l2 2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>,
  // Code brackets / Development
  <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7"><path d="M12 8L4 16l8 8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M20 8l8 8-8 8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  // Rocket / Launch
  <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7"><path d="M16 4c-4 6-4 14-2 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/><path d="M16 4c4 6 4 14 2 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/><circle cx="16" cy="14" r="2.5" stroke="currentColor" strokeWidth="2"/><path d="M10 22c-2 2-3 5-3 6 1 0 4-1 6-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M22 22c2 2 3 5 3 6-1 0-4-1-6-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
];

const ProcessSection = ({ tag, title, docs, steps: contentSteps }: ProcessSectionProps) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const torusY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const torusRotate = useTransform(scrollYProgress, [0, 1], [-15, 25]);
  const spiralY = useTransform(scrollYProgress, [0, 1], [-40, 80]);
  const spiralRotate = useTransform(scrollYProgress, [0, 1], [10, -20]);
  const fallbackSteps = steps.map((step) => ({
    title: t(`process.${step}.title`),
    description: t(`process.${step}.desc`),
  }));
  const resolvedSteps = contentSteps?.length ? contentSteps : fallbackSteps;

  return (
    <section ref={sectionRef} className="section-y relative overflow-hidden" id="process">
      {/* Large decorative chrome assets */}
      <motion.img
        src={assetTorus}
        alt=""
        className="absolute -right-16 top-20 w-[280px] lg:w-[380px] opacity-[0.12] pointer-events-none select-none"
        style={{ y: torusY, rotate: torusRotate }}
        loading="lazy"
      />
      <motion.img
        src={assetSpiral}
        alt=""
        className="absolute -left-20 bottom-10 w-[200px] lg:w-[300px] opacity-[0.08] pointer-events-none select-none"
        style={{ y: spiralY, rotate: spiralRotate }}
        loading="lazy"
      />

      <div ref={ref} className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="section-head max-w-2xl"
        >
          <p className="font-body text-xs tracking-[0.2em] text-muted-foreground mb-3 uppercase">
            {tag || t('process.tag')}
          </p>
          <h2 className="font-display text-4xl lg:text-6xl font-bold text-foreground">
            {title || t('process.title')}
          </h2>
        </motion.div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
          {resolvedSteps.map((step, i) => (
            <motion.div
              key={`${step.title}-${i}`}
              initial={{ opacity: 0, y: 50, scale: 0.96 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, transition: { type: 'spring', stiffness: 300 } }}
              className="group cursor-default"
            >
              <SpotlightCard className="rounded-2xl p-8 lg:p-10">
              <div className="relative z-10 flex items-start gap-6">
                {/* Icon container */}
                <motion.div
                  className="flex-shrink-0 w-14 h-14 rounded-xl border border-border/80 bg-secondary/50 flex items-center justify-center text-foreground/70 group-hover:text-foreground group-hover:border-foreground/20 transition-all duration-500"
                  whileHover={{ rotate: 5, scale: 1.05 }}
                >
                  {stepIcons[i % stepIcons.length]}
                </motion.div>

                <div className="flex-1 min-w-0">
                  {/* Step number */}
                  <span className="font-display text-[10px] tracking-[0.25em] text-muted-foreground/50 uppercase block mb-3">
                    {t(`process.step${i + 1}.badge`)}
                  </span>
                  <h3 className="font-display text-xl lg:text-2xl font-semibold text-foreground mb-3 group-hover:translate-x-1 transition-transform duration-300">
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Bottom accent line */}
              </SpotlightCard>
            </motion.div>
          ))}
        </div>

        {/* Documentation callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 lg:mt-16 relative rounded-2xl border border-border/40 bg-secondary/30 p-8 lg:p-10 overflow-hidden"
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(135deg, hsl(var(--foreground) / 0.02) 0%, transparent 60%)',
          }} />
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-5">
            {/* Document icon */}
            <div className="flex-shrink-0 w-12 h-12 rounded-xl border border-border/60 bg-background flex items-center justify-center text-foreground/60">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="font-body text-sm lg:text-base text-muted-foreground leading-relaxed">
              {docs || t('process.docs')}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;
